import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { IntrospectViewer } from '@adornis/introspect/client/x-introspect-viewer.js';
import { XEntityVersionPatchList } from '@adornis/versioned-entity/client/x-entity-version-patch-list.js';
import { html, nothing } from 'lit';
import { MailProcessing } from '../../features/information-extraction/db/MailExtractionResult.js';
import { DeliveryDateExplanation } from './DeliveryDateExplanation.js';
import { ExtractionTimeline } from './ExtractionTimeline.js';
import { RequestCorrespondence } from './RequestCorrespondence.js';

type SecondaryTab = 'notizen' | 'nachvollziehen' | 'mailverkehr' | 'verlauf';

/**
 * The open tab, remembered per request beyond the life of this component.
 *
 * A directive's state dies with its part, and this card sits in a panel that lit rebuilds
 * whenever the pane switches branches — an extraction starting, the request coming back from
 * the server. Without a memory outside it, following an answer mail in "Mailverkehr" dropped
 * you back on "Notizen" the moment that answer was read, which is the one place you were not
 * looking. Keyed by mail id so two requests do not inherit each other's tab.
 */
const openTabByRequest = new Map<string, SecondaryTab>();

interface SecondaryTabsProps {
  mailID: string;
  mailProcessing: MailProcessing;
  isExtracting: boolean;
  introspectionNodeID?: string | null;
  canonicalDocument?: string | null;
  onDownloadCanonical?: () => void;
}

// Demoted, on-demand detail of a request, folded into a single tabbed card so the main view
// stays focused on company + products. Replaces the old stack of separate "Mehr" accordions.
export const SecondaryTabs = stateful((props: SecondaryTabsProps) => {
  const { mailID, mailProcessing, isExtracting, introspectionNodeID, canonicalDocument, onDownloadCanonical } = props;
  const [tab, setTab] = useState<SecondaryTab>(openTabByRequest.get(mailID) ?? 'notizen', [mailID]);
  const selectTab = (next: SecondaryTab) => {
    openTabByRequest.set(mailID, next);
    setTab(next);
  };
  const [newNote, setNewNote] = useState('');

  const notes = mailProcessing.notes ?? [];

  const tabs: { key: SecondaryTab; label: string; badge?: number }[] = [
    { key: 'notizen', label: 'Notizen', badge: notes.length || undefined },
    { key: 'nachvollziehen', label: 'Nachvollziehen' },
    { key: 'mailverkehr', label: 'Mailverkehr' },
    { key: 'verlauf', label: 'Versionshistorie' },
  ];

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    mailProcessing.updateNotes().add(newNote.trim());
    await mailProcessing.save();
    setNewNote('');
  };

  const handleDeleteNote = async (index: number) => {
    mailProcessing.updateNotes().delete(index);
    await mailProcessing.save();
    setNewNote(newNote);
  };

  const renderNotizen = () => html`
    <div class="flex gap-2">
      <input
        type="text"
        class="input input-bordered input-sm flex-1"
        .value=${newNote}
        @input=${(e: Event) => setNewNote((e.target as HTMLInputElement).value)}
        @keypress=${(e: KeyboardEvent) => {
          if (e.key === 'Enter') handleAddNote();
        }}
        placeholder="Notiz hinzufügen…"
      />
      <button class="btn btn-neutral btn-sm gap-1" @click=${handleAddNote} ?disabled=${!newNote.trim()}>
        <x-icon class="text-sm">add</x-icon> Hinzufügen
      </button>
    </div>
    ${notes.length > 0
      ? html`
          <div class="space-y-2 mt-3">
            ${notes.map(
              (note, index) => html`
                <div class="flex items-center justify-between gap-2 rounded-lg bg-base-200/50 px-3 py-2">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <x-icon class="text-sm text-base-content/40">sticky_note_2</x-icon>
                    <span class="flex-1 text-sm">${note}</span>
                  </div>
                  <button class="btn btn-ghost btn-xs btn-square" @click=${() => handleDeleteNote(index)}>
                    <x-icon class="text-sm">close</x-icon>
                  </button>
                </div>
              `,
            )}
          </div>
        `
      : html`<div class="text-sm text-base-content/40 mt-3">Noch keine Notizen zu dieser Anfrage.</div>`}
  `;

  const renderNachvollziehen = () => {
    if (!introspectionNodeID)
      return html`<div class="text-sm text-base-content/40">Noch keine Extraktion vorhanden.</div>`;
    return html`
      ${ExtractionTimeline({ mailID, live: isExtracting, canonicalDocument, onDownloadCanonical })}

      <!--
        Eigener Abschnitt fuer den Liefertermin: Er ist das einzige Feld im Angebot, das nicht
        aus der Anfrage stammt, sondern gerechnet wird. Ohne Herleitung bleibt er eine Zahl,
        der man glauben muss.
      -->
      <div class="border-t border-base-200 mt-2 pt-4">
        <div class="flex items-center gap-2 mb-3">
          <x-icon class="text-sm text-base-content/40">local_shipping</x-icon>
          <h3 class="text-[10.5px] uppercase tracking-wider font-semibold text-base-content/40">
            Wie der Liefertermin zustande kommt
          </h3>
        </div>
        ${DeliveryDateExplanation({ extractionResult: mailProcessing.extractionResult })}
      </div>

      <div class="collapse collapse-arrow rounded-none border-t border-base-200 mt-2">
        <input type="checkbox" />
        <div class="collapse-title text-sm font-medium text-base-content/50 flex items-center gap-2">
          <x-icon class="text-sm">code</x-icon> Technische Details
        </div>
        <div class="collapse-content">${IntrospectViewer({ nodeID: introspectionNodeID })}</div>
      </div>
    `;
  };

  const renderVerlauf = () => html`
    ${XEntityVersionPatchList({
      referenceIDs: [mailProcessing._id],
      collectionName: MailProcessing._versionCollection,
    })}
  `;

  return html`
    <div class="rounded-xl bg-base-100 border border-base-200 overflow-hidden">
      <div class="flex gap-1 p-1.5 border-b border-base-200 bg-base-200/40">
        ${tabs.map(
          t => html`
            <button
              class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] transition ${tab === t.key
                ? 'bg-base-100 text-base-content font-semibold shadow-sm'
                : 'text-base-content/55 font-medium hover:text-base-content/80'}"
              @click=${() => selectTab(t.key)}
            >
              ${t.label}
              ${t.badge ? html`<span class="badge badge-neutral badge-xs">${t.badge}</span>` : nothing}
            </button>
          `,
        )}
      </div>
      <div class="p-5">
        ${tab === 'notizen'
          ? renderNotizen()
          : tab === 'nachvollziehen'
            ? renderNachvollziehen()
            : tab === 'mailverkehr'
              ? RequestCorrespondence({ mailID })
              : renderVerlauf()}
      </div>
    </div>
  `;
});
